import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { DownloadSimple, FileCsv, FilePdf, Trash, ArrowsClockwise, CheckCircle, WarningCircle, UsersThree, ArrowUUpLeft } from "@phosphor-icons/react";

const leadStatusBadge = {
  New: "bg-blue-50 text-[#002FA7] border border-[#002FA7]/20",
  Prepared: "bg-amber-50 text-amber-700 border border-amber-200",
  Sent: "bg-[#00796B]/10 text-[#00796B] border border-[#00796B]/20",
  "E-mail Versand": "bg-violet-50 text-violet-700 border border-violet-200",
  "Rückläufer": "bg-orange-50 text-orange-700 border border-orange-200",
  "E-Mail bounced": "bg-red-50 text-[#E53935] border border-red-200",
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Versand() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipBatch, setRecipBatch] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipLoading, setRecipLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/versand");
      setBatches(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh while any batch is still generating.
  useEffect(() => {
    if (!batches.some((b) => b.status === "generating")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [batches]);

  const isReady = (b) => !b.status || b.status === "ready";

  const toggleSent = async (batch) => {
    try {
      const { data } = await api.patch(`/versand/${batch.id}`, { sent: !batch.sent });
      setBatches((bs) => bs.map((b) => (b.id === batch.id ? data : b)));
      toast.success(data.sent ? "Als versendet markiert (Leads → Sent)" : "Versand-Markierung entfernt (Leads → Prepared)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadPdf = async (batch) => {
    try {
      const res = await api.get(`/versand/${batch.id}/pdf`, { responseType: "blob" });
      downloadBlob(res.data, `${batch.name}.pdf`);
    } catch (err) {
      toast.error("PDF-Download fehlgeschlagen");
    }
  };

  const downloadCsv = async (batch) => {
    try {
      const res = await api.get(`/versand/${batch.id}/leads.csv`, { responseType: "blob" });
      downloadBlob(res.data, `${batch.name}.csv`);
    } catch (err) {
      toast.error("CSV-Download fehlgeschlagen");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Diesen Versand-Eintrag inkl. gespeicherter PDF löschen?")) return;
    await api.delete(`/versand/${id}`);
    setBatches((bs) => bs.filter((b) => b.id !== id));
    toast.success("Versand-Eintrag gelöscht");
  };

  const openRecipients = async (batch) => {
    setRecipBatch(batch);
    setRecipLoading(true);
    try {
      const { data } = await api.get(`/versand/${batch.id}/leads`);
      setRecipients(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Empfänger konnten nicht geladen werden");
    } finally {
      setRecipLoading(false);
    }
  };

  const toggleReturned = async (lead) => {
    const newStatus = lead.status === "Rückläufer" ? "Sent" : "Rückläufer";
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { status: newStatus });
      setRecipients((rs) => rs.map((r) => (r.id === lead.id ? { ...r, status: data.status } : r)));
      toast.success(newStatus === "Rückläufer" ? `${lead.name}: als Rückläufer markiert` : `${lead.name}: Rückläufer-Markierung entfernt`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const StatusCell = ({ b }) => {
    if (b.status === "generating")
      return (
        <span className="inline-flex items-center gap-1 text-xs text-amber-700" data-testid={`status-${b.id}`}>
          <ArrowsClockwise size={13} className="animate-spin" /> wird erstellt…
        </span>
      );
    if (b.status === "error")
      return (
        <span className="inline-flex items-center gap-1 text-xs text-[#E53935]" title={b.error} data-testid={`status-${b.id}`}>
          <WarningCircle size={14} weight="fill" /> Fehler
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[#00796B]" data-testid={`status-${b.id}`}>
        <CheckCircle size={14} weight="fill" /> Bereit
      </span>
    );
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold tracking-tight">Versand</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Jede generierte PDF erscheint hier. Lade PDF oder Empfänger-CSV herunter und markiere den Versand.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-12 text-center">Lade…</div>
      ) : batches.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-md p-12 text-center text-zinc-500">
          Noch keine PDFs generiert. Erstelle unter „Leads" einen Serienbrief.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto" data-testid="versand-table">
          <table className="w-full text-sm border-collapse min-w-[980px]">
            <thead>
              <tr className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium">Bezeichnung</th>
                <th className="px-4 py-3 font-medium">Vorlage</th>
                <th className="px-4 py-3 font-medium">Empfänger</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Erstellt</th>
                <th className="px-4 py-3 font-medium">Versendet</th>
                <th className="px-4 py-3 font-medium text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} data-testid="versand-row" className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium max-w-[280px]"><div className="truncate" title={b.name}>{b.name}</div></td>
                  <td className="px-4 py-3 text-zinc-600">{b.template_name}</td>
                  <td className="px-4 py-3"><Badge variant="secondary" className="rounded-sm font-normal">{b.lead_count}</Badge></td>
                  <td className="px-4 py-3"><StatusCell b={b} /></td>
                  <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{fmt(b.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={b.sent} onCheckedChange={() => toggleSent(b)} disabled={!isReady(b)} data-testid={`sent-toggle-${b.id}`} />
                      {b.sent && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#00796B]/10 text-[#00796B]">Versendet</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openRecipients(b)} data-testid={`recipients-${b.id}`} className="h-8 text-xs">
                        <UsersThree size={15} className="mr-1" /> Empfänger
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(b)} disabled={!isReady(b)} data-testid={`download-pdf-${b.id}`} className="h-8 text-xs">
                        <FilePdf size={15} className="mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadCsv(b)} data-testid={`download-csv-${b.id}`} className="h-8 text-xs">
                        <FileCsv size={15} className="mr-1" /> CSV
                      </Button>
                      <button onClick={() => remove(b.id)} data-testid={`delete-versand-${b.id}`} className="ml-1 text-zinc-300 hover:text-[#E53935]">
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recipient list dialog with Rückläufer toggle */}
      <Dialog open={!!recipBatch} onOpenChange={(o) => !o && setRecipBatch(null)}>
        <DialogContent className="max-w-3xl" data-testid="recipients-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Empfänger · {recipBatch?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500 -mt-2">
            {recipients.length} Empfänger · {recipients.filter((r) => r.status === "Rückläufer").length} Rückläufer.
            Hake zurückgekommene Briefe ab – der Lead-Status wechselt auf „Rückläufer".
          </p>
          {recipLoading ? (
            <div className="text-sm text-zinc-400 py-8 text-center">Lade Empfänger…</div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto border border-zinc-200 rounded-md">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-zinc-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-3 py-2 font-medium">ID</th>
                    <th className="px-3 py-2 font-medium">Firma</th>
                    <th className="px-3 py-2 font-medium">Adresse</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium text-right">Rückläufer</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r) => (
                    <tr key={r.id} data-testid="recipient-row" className={`border-t border-zinc-100 ${r.status === "Rückläufer" ? "bg-orange-50/50" : ""}`}>
                      <td className="px-3 py-2 text-zinc-400 tabular-nums text-xs">#{String(r.lead_no ?? "").padStart(4, "0")}</td>
                      <td className="px-3 py-2 font-medium max-w-[220px]"><div className="truncate" title={r.name}>{r.name}</div></td>
                      <td className="px-3 py-2 text-zinc-600 text-xs max-w-[240px]">
                        <div className="truncate" title={`${r.street || ""}, ${r.plz || ""} ${r.city || ""}`}>
                          {r.street}{r.street && (r.plz || r.city) ? ", " : ""}{r.plz} {r.city}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-sm whitespace-nowrap ${leadStatusBadge[r.status] || "bg-zinc-100 text-zinc-600"}`} data-testid={`recipient-status-${r.id}`}>
                          {r.status || "New"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end">
                          <Switch checked={r.status === "Rückläufer"} onCheckedChange={() => toggleReturned(r)} data-testid={`returned-toggle-${r.id}`} />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {recipients.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-400">Keine Empfänger gefunden (Leads evtl. gelöscht).</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
